import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, getDocs, serverTimestamp } from "firebase/firestore";
import { db } from "../../../lib/firebase";
import {
  createEncounter,
  createPatient,
} from "../../../services/patientService";
import {
  buildFullName,
  buildFullNameLower,
  calculateAgeFromDob,
  normalizePhone,
  normalizeSpaces,
  toTitleCase,
} from "../../../utils/patient/patientUtils";
import {
  SectionCard,
  AutoTextarea,
  buttonStyle,
  primaryButtonStyle,
  createButtonStyle,
  inputStyle,
  labelStyle,
  getEmployeeDisplayName,
  isProviderEmployee,
  buildBpString,
  buildTempString,
  getGcsTotal,
  digitsOnly,
} from "../../../components/patientChart/patientChartShared";
import VitalsSection from "../../../components/patientChart/sections/VitalsSection";
import MedicalSection from "../../../components/patientChart/sections/MedicalSection";
import TreatmentSection from "../../../components/patientChart/TreatmentSection";
import SignatureSection from "../../../components/patientChart/SignatureSection";
import { buildTreatmentNarrative } from "../../../components/patientChart/treatmentUtils";

const pageStyle = {
  background: "linear-gradient(180deg, #eef4ff 0%, #f8fafc 100%)",
  minHeight: "100vh",
  padding: 18,
};

const initialPatient = {
  firstName: "",
  lastName: "",
  dob: "",
  age: "",
  gender: "",
  phone: "",
  email: "",
  streetAddress: "",
  city: "",
  state: "",
  zip: "",
  allergies: "",
  allergiesNone: false,
  medications: "",
  medicationsNone: false,
};

const blankEncounterForm = {
  status: "open",
  arrivalDate: new Date().toISOString().slice(0, 10),
  arrivalTime: new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  }),
  chiefComplaint: "",
  bed: "",
  seenAt: "Course",
  source: "Responder",
  providerName: "",
  reportCompletedBy: "",
  notes: "",
};

const blankMedicalForm = {
  hpiComplaint: "",
  assessment: "",
  planTreatmentDisposition: "",
  treatments: [],
  bodyPartInjured: "",
  firstAidGiven: false,
  disposition: "",
};

const blankVital = () => ({
  time: new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  }),
  systolic: "",
  diastolic: "",
  bp: "",
  pulse: "",
  resp: "",
  spo2: "",
  tempValue: "",
  tempUnit: "F",
  temp: "",
  pain: "",
  gcsEye: "",
  gcsVerbal: "",
  gcsMotor: "",
  gcs: "",
  etco2: "",
  rhythm: "",
  rhythmOther: "",
  notes: "",
});

const defaultExpandedSections = {
  patientInfo: true,
  summary: true,
  vitals: true,
  medical: true,
  treatments: true,
  signatures: true,
};

function formatPhoneInput(value = "") {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
  if (!digits) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function buildAddressString(form) {
  const street = normalizeSpaces(form.streetAddress);
  const city = normalizeSpaces(form.city);
  const state = normalizeSpaces(form.state);
  const zip = normalizeSpaces(form.zip);
  const cityState = [city, state].filter(Boolean).join(", ");
  const line2 = [cityState, zip].filter(Boolean).join(" ");
  return [street, line2].filter(Boolean).join(", ");
}

export default function ResponderPCRPage() {
  const [payload, setPayload] = useState(null);
  const [patientForm, setPatientForm] = useState(initialPatient);
  const [encounterForm, setEncounterForm] = useState(blankEncounterForm);
  const [medicalForm, setMedicalForm] = useState(blankMedicalForm);
  const [vitalsForm, setVitalsForm] = useState([blankVital()]);
  const [signatureForm, setSignatureForm] = useState({
    patientSignature: "",
    providerSignature: "",
    unableToObtain: false,
  });
  const [employees, setEmployees] = useState([]);
  const [expandedSections, setExpandedSections] = useState(defaultExpandedSections);
  const [activeSection, setActiveSection] = useState("patientInfo");
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const patientInfoRef = useRef(null);
  const summaryRef = useRef(null);
  const vitalsRef = useRef(null);
  const medicalRef = useRef(null);
  const treatmentsRef = useRef(null);
  const signaturesRef = useRef(null);

  useEffect(() => {
    try {
      const stored = JSON.parse(sessionStorage.getItem("activePCR") || "{}");
      setPayload(stored || null);

      setEncounterForm((prev) => ({
        ...prev,
        source: stored?.unitLabel || "Responder",
        seenAt: "Course",
      }));
    } catch {
      setPayload(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadEmployees() {
      try {
        const snap = await getDocs(collection(db, "users"));
        if (cancelled) return;
        setEmployees(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Error loading employees:", err);
      }
    }

    loadEmployees();
    return () => {
      cancelled = true;
    };
  }, []);

  const providerOptions = useMemo(
    () => employees.filter(isProviderEmployee).map(getEmployeeDisplayName).filter(Boolean).sort(),
    [employees]
  );

  const reportCompletedOptions = useMemo(
    () => employees.map(getEmployeeDisplayName).filter(Boolean).sort(),
    [employees]
  );

  function toggleSection(sectionKey) {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  }

  function setPatientField(field, value) {
    setPatientForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "dob") next.age = calculateAgeFromDob(value);
      if (field === "phone") next.phone = formatPhoneInput(value);
      if (field === "state") next.state = String(value || "").replace(/[^a-z]/gi, "").toUpperCase().slice(0, 2);
      if (field === "zip") next.zip = String(value || "").replace(/\D/g, "").slice(0, 5);

      return next;
    });
  }

  function setEncounterField(field, value) {
    setEncounterForm((prev) => ({ ...prev, [field]: value }));
  }

  function setMedicalField(field, value) {
    setMedicalForm((prev) => ({ ...prev, [field]: value }));
  }

  function setSignatureField(field, value) {
    setSignatureForm((prev) => ({ ...prev, [field]: value }));
  }

  function addVital() {
    setVitalsForm((prev) => [...prev, blankVital()]);
  }

  function removeVital(index) {
    setVitalsForm((prev) => prev.filter((_, i) => i !== index));
  }

  function setVitalField(index, field, value) {
    setVitalsForm((prev) =>
      prev.map((vital, i) => {
        if (i !== index) return vital;

        const next = { ...vital, [field]: value };

        if (field === "systolic" || field === "diastolic") {
          next[field] = digitsOnly(value).slice(0, 3);
          next.bp = buildBpString(
            field === "systolic" ? next.systolic : vital.systolic,
            field === "diastolic" ? next.diastolic : vital.diastolic
          );
        }

        if (["pulse", "resp", "spo2", "etco2"].includes(field)) {
          next[field] = digitsOnly(value).slice(0, 3);
        }

        if (field === "tempValue") {
          next.tempValue = String(value || "").replace(/[^\d.]/g, "").slice(0, 5);
          next.temp = buildTempString(next.tempValue, next.tempUnit);
        }

        if (field === "tempUnit") {
          next.tempUnit = value || "F";
          next.temp = buildTempString(next.tempValue, next.tempUnit);
        }

        if (["gcsEye", "gcsVerbal", "gcsMotor"].includes(field)) {
          next.gcs = getGcsTotal(
            field === "gcsEye" ? value : next.gcsEye,
            field === "gcsVerbal" ? value : next.gcsVerbal,
            field === "gcsMotor" ? value : next.gcsMotor
          );
        }

        if (field === "rhythm" && value !== "Other") next.rhythmOther = "";

        return next;
      })
    );
  }

  function serializeVitalsForSave() {
    return vitalsForm.map((vital) => {
      const bp = buildBpString(vital.systolic, vital.diastolic);
      const gcs = getGcsTotal(vital.gcsEye, vital.gcsVerbal, vital.gcsMotor);
      const temp = buildTempString(vital.tempValue, vital.tempUnit);

      return {
        ...vital,
        bp,
        gcs,
        temp,
        rhythmOther: vital.rhythm === "Other" ? vital.rhythmOther || "" : "",
      };
    });
  }

  function serializeMedicalForSave() {
    return {
      hpiComplaint: medicalForm.hpiComplaint || "",
      complaintNotes: medicalForm.hpiComplaint || "",
      historyPresentIllness: medicalForm.hpiComplaint || "",
      assessment: medicalForm.assessment || "",
      planTreatmentDisposition: medicalForm.planTreatmentDisposition || "",
      treatments: Array.isArray(medicalForm.treatments) ? medicalForm.treatments : [],
      treatmentRendered: buildTreatmentNarrative(medicalForm.treatments || []),
      disposition: medicalForm.disposition || "",
      dispositionNotes: medicalForm.planTreatmentDisposition || "",
      notes: medicalForm.planTreatmentDisposition || "",
      followUpNotes: "",
    };
  }

  function validate() {
    if (!normalizeSpaces(patientForm.firstName)) return "First name is required.";
    if (!normalizeSpaces(patientForm.lastName)) return "Last name is required.";
    if (!normalizeSpaces(encounterForm.chiefComplaint)) return "Chief complaint is required.";
    if (!normalizeSpaces(encounterForm.providerName)) return "Provider is required.";
    if (!normalizeSpaces(encounterForm.reportCompletedBy)) return "Report Completed By is required.";
    if (!normalizeSpaces(medicalForm.hpiComplaint)) return "HPI / Complaint is required.";
    if (!normalizeSpaces(medicalForm.assessment)) return "Assessment is required.";
    if (!normalizeSpaces(medicalForm.disposition)) return "Disposition is required.";
    if (!normalizeSpaces(medicalForm.planTreatmentDisposition)) return "Plan / Treatment / Disposition is required.";
    if (!signatureForm.unableToObtain && !signatureForm.patientSignature) return "Patient signature is required or mark Unable to Obtain.";
    return "";
  }

  async function handleSubmitPCR() {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError("");

    try {
      const firstName = toTitleCase(patientForm.firstName);
      const lastName = toTitleCase(patientForm.lastName);

      const patientPayload = {
        firstName,
        lastName,
        fullName: buildFullName(firstName, lastName),
        fullNameLower: buildFullNameLower(firstName, lastName),
        dob: normalizeSpaces(patientForm.dob),
        age: normalizeSpaces(patientForm.age),
        gender: normalizeSpaces(patientForm.gender),
        phone: normalizeSpaces(patientForm.phone),
        phoneDigitsOnly: normalizePhone(patientForm.phone),
        email: normalizeSpaces(patientForm.email),
        streetAddress: normalizeSpaces(patientForm.streetAddress),
        city: normalizeSpaces(patientForm.city),
        state: normalizeSpaces(patientForm.state),
        zip: normalizeSpaces(patientForm.zip),
        address: buildAddressString(patientForm),
        allergies: patientForm.allergiesNone ? "" : normalizeSpaces(patientForm.allergies),
        allergiesNone: Boolean(patientForm.allergiesNone),
        medications: patientForm.medicationsNone ? "" : normalizeSpaces(patientForm.medications),
        medicationsNone: Boolean(patientForm.medicationsNone),
        lastEncounterAt: null,
        encounterCount: 0,
        createdFrom: "responderPCR",
        createdByUnitId: payload?.unitId || "",
        createdByUnitLabel: payload?.unitLabel || "",
      };

      const patientId = await createPatient(patientPayload);

      await createEncounter(patientId, {
        currentEncounterCount: 0,
        ...encounterForm,
        status: "open",
        source: encounterForm.source || payload?.unitLabel || "Responder",
        seenAt: encounterForm.seenAt || "Course",
        medical: serializeMedicalForSave(),
        vitals: serializeVitalsForSave(),
        signature: signatureForm,
        documents: [],
        createdFrom: "responderPCR",
        responderPCR: true,
        responderSubmittedAt: serverTimestamp(),
        responderUnitId: payload?.unitId || "",
        responderUnitLabel: payload?.unitLabel || "",
        responderUnitSlug: payload?.unitSlug || "",
        dispatchCallId: payload?.call?.id || "",
        dispatchCall: payload?.call || null,
      });

      sessionStorage.removeItem("activePCR");
      setSubmitted(true);

      try {
        window.opener?.postMessage({ type: "pcr:submitted" }, window.location.origin);
      } catch {
        // ignore
      }
    } catch (err) {
      console.error("Responder PCR submit error:", err);
      setError(err?.message || "Could not submit PCR.");
    } finally {
      setSaving(false);
    }
  }

  if (submitted) {
    return (
      <div style={pageStyle}>
        <div
          style={{
            maxWidth: 720,
            margin: "40px auto",
            background: "#fff",
            border: "1px solid #bbf7d0",
            borderRadius: 18,
            padding: 24,
            textAlign: "center",
          }}
        >
          <h1 style={{ marginTop: 0, color: "#166534" }}>PCR Submitted</h1>
          <p style={{ color: "#475569" }}>
            The PCR has been submitted to HQ. This window can be closed.
          </p>
          <button type="button" style={primaryButtonStyle} onClick={() => window.close()}>
            Close Window
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <datalist id="provider-options">
        {providerOptions.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>

      <datalist id="report-completed-options">
        {reportCompletedOptions.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 28, fontWeight: 900 }}>Responder PCR</div>
          <div style={{ color: "#64748b", marginTop: 4 }}>
            {payload?.unitLabel || "Responder Unit"} · Submit-only charting
          </div>
        </div>

        <button type="button" style={createButtonStyle} onClick={handleSubmitPCR} disabled={saving}>
          {saving ? "Submitting..." : "Submit PCR"}
        </button>
      </div>

      {error && (
        <div
          style={{
            border: "1px solid #fecaca",
            background: "#fef2f2",
            color: "#b91c1c",
            borderRadius: 14,
            padding: 12,
            marginBottom: 14,
            fontWeight: 700,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 16 }}>
        <div
          style={{
            position: "sticky",
            top: 12,
            background: "#173b6d",
            borderRadius: 18,
            padding: 12,
            display: "grid",
            gap: 8,
            alignSelf: "start",
          }}
        >
          {[
            ["patientInfo", "Patient Info"],
            ["summary", "Summary"],
            ["vitals", "Vitals"],
            ["medical", "Medical"],
            ["treatments", "Treatments"],
            ["signatures", "Signature"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveSection(key)}
              style={{
                border: "none",
                borderRadius: 12,
                padding: "14px 16px",
                background: activeSection === key ? "#fff" : "rgba(255,255,255,.08)",
                color: activeSection === key ? "#173b6d" : "#fff",
                fontWeight: 800,
                textAlign: "left",
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <div style={{ display: "grid", gap: 14 }}>
          {activeSection === "patientInfo" && (
            <SectionCard
              title="Patient Information"
              sectionKey="patientInfo"
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              sectionRef={patientInfoRef}
              activeSection={activeSection}
            >
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                <label style={labelStyle}>First Name *<input style={inputStyle} value={patientForm.firstName} onChange={(e) => setPatientField("firstName", e.target.value)} /></label>
                <label style={labelStyle}>Last Name *<input style={inputStyle} value={patientForm.lastName} onChange={(e) => setPatientField("lastName", e.target.value)} /></label>
                <label style={labelStyle}>DOB<input type="date" style={inputStyle} value={patientForm.dob} onChange={(e) => setPatientField("dob", e.target.value)} /></label>
                <label style={labelStyle}>Age<input style={inputStyle} value={patientForm.age} onChange={(e) => setPatientField("age", e.target.value)} /></label>
                <label style={labelStyle}>Gender<select style={inputStyle} value={patientForm.gender} onChange={(e) => setPatientField("gender", e.target.value)}><option value="">Select</option><option value="Male">Male</option><option value="Female">Female</option></select></label>
                <label style={labelStyle}>Phone<input style={inputStyle} value={patientForm.phone} onChange={(e) => setPatientField("phone", e.target.value)} /></label>
                <label style={labelStyle}>Email<input style={inputStyle} value={patientForm.email} onChange={(e) => setPatientField("email", e.target.value)} /></label>
                <label style={labelStyle}>Street Address<input style={inputStyle} value={patientForm.streetAddress} onChange={(e) => setPatientField("streetAddress", e.target.value)} /></label>
                <label style={labelStyle}>City<input style={inputStyle} value={patientForm.city} onChange={(e) => setPatientField("city", e.target.value)} /></label>
                <label style={labelStyle}>State<input style={inputStyle} value={patientForm.state} onChange={(e) => setPatientField("state", e.target.value)} /></label>
                <label style={labelStyle}>Zip<input style={inputStyle} value={patientForm.zip} onChange={(e) => setPatientField("zip", e.target.value)} /></label>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10, marginTop: 12 }}>
                <label style={labelStyle}>
                  Allergies
                  <AutoTextarea value={patientForm.allergies} onChange={(e) => setPatientField("allergies", e.target.value)} />
                  <span><input type="checkbox" checked={patientForm.allergiesNone} onChange={(e) => setPatientField("allergiesNone", e.target.checked)} /> None</span>
                </label>

                <label style={labelStyle}>
                  Medications
                  <AutoTextarea value={patientForm.medications} onChange={(e) => setPatientField("medications", e.target.value)} />
                  <span><input type="checkbox" checked={patientForm.medicationsNone} onChange={(e) => setPatientField("medicationsNone", e.target.checked)} /> None</span>
                </label>
              </div>
            </SectionCard>
          )}

          {activeSection === "summary" && (
            <SectionCard
              title="Encounter Summary"
              sectionKey="summary"
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              sectionRef={summaryRef}
              activeSection={activeSection}
            >
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                <label style={labelStyle}>Arrival Date<input type="date" style={inputStyle} value={encounterForm.arrivalDate} onChange={(e) => setEncounterField("arrivalDate", e.target.value)} /></label>
                <label style={labelStyle}>Arrival Time<input style={inputStyle} value={encounterForm.arrivalTime} onChange={(e) => setEncounterField("arrivalTime", e.target.value)} /></label>
                <label style={labelStyle}>Seen At<select style={inputStyle} value={encounterForm.seenAt} onChange={(e) => setEncounterField("seenAt", e.target.value)}><option value="Course">Course</option><option value="Tent">Tent</option><option value="HQ">HQ</option><option value="Other">Other</option></select></label>
                <label style={labelStyle}>Source<input style={inputStyle} value={encounterForm.source} onChange={(e) => setEncounterField("source", e.target.value)} /></label>
                <label style={labelStyle}>Chief Complaint *<input style={inputStyle} value={encounterForm.chiefComplaint} onChange={(e) => setEncounterField("chiefComplaint", e.target.value)} /></label>
                <label style={labelStyle}>Provider *<input list="provider-options" style={inputStyle} value={encounterForm.providerName} onChange={(e) => setEncounterField("providerName", e.target.value)} /></label>
                <label style={labelStyle}>Report Completed By *<input list="report-completed-options" style={inputStyle} value={encounterForm.reportCompletedBy} onChange={(e) => setEncounterField("reportCompletedBy", e.target.value)} /></label>
              </div>
            </SectionCard>
          )}

          {activeSection === "vitals" && (
            <VitalsSection
              selectedEncounterId="responder-draft"
              vitalsForm={vitalsForm}
              addVital={addVital}
              removeVital={removeVital}
              setVitalField={setVitalField}
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              sectionRef={vitalsRef}
              activeSection={activeSection}
            />
          )}

          {activeSection === "medical" && (
            <MedicalSection
              selectedEncounterId="responder-draft"
              medicalForm={medicalForm}
              setMedicalField={setMedicalField}
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              sectionRef={medicalRef}
              activeSection={activeSection}
            />
          )}

          {activeSection === "treatments" && (
            <SectionCard
              title="Treatments"
              sectionKey="treatments"
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              sectionRef={treatmentsRef}
              activeSection={activeSection}
            >
              <TreatmentSection
                treatments={medicalForm.treatments || []}
                setTreatments={(updater) =>
                  setMedicalForm((prev) => ({
                    ...prev,
                    treatments:
                      typeof updater === "function"
                        ? updater(prev.treatments || [])
                        : updater,
                  }))
                }
                employeeOptions={reportCompletedOptions}
                defaultPerformedBy={encounterForm.reportCompletedBy}
              />
            </SectionCard>
          )}

          {activeSection === "signatures" && (
            <SignatureSection
              selectedEncounterId="responder-draft"
              signatureForm={signatureForm}
              setSignatureField={setSignatureField}
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              sectionRef={signaturesRef}
              activeSection={activeSection}
            />
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button type="button" style={buttonStyle} onClick={() => window.close()} disabled={saving}>
              Cancel
            </button>
            <button type="button" style={primaryButtonStyle} onClick={handleSubmitPCR} disabled={saving}>
              {saving ? "Submitting..." : "Submit PCR"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}